import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import Breadcrumb from '@/components/base/Breadcrumb';
import Pagination from '@/components/base/Pagination';
import ComicCard from '@/components/feature/ComicCard';
import { comics } from '@/mocks/comics';

const GENRES = ['热血', '科幻', '恋爱', '日常', '奇幻', '悬疑', '古风', '玄幻', '搞笑'];
const STATUS = ['连载中', '已完结'];
const REGIONS = ['国漫', '日本', '韩国', '欧美'];
const UPDATED = [
  { label: '近一周', days: 7 },
  { label: '近一月', days: 31 },
];
const SORTS = [
  { key: 'updated', label: '更新时间' },
  { key: 'popularity', label: '新增热度' },
  { key: 'favorites', label: '收藏数' },
];
const REFERENCE_DATE = new Date('2026-10-13');
const PER_PAGE = 6;

function daysBetween(dateStr: string) {
  const target = new Date(dateStr);
  return Math.floor((REFERENCE_DATE.getTime() - target.getTime()) / (1000 * 60 * 60 * 24));
}

export default function ComicsCategoryPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  const genre = searchParams.get('genre') || '';
  const status = searchParams.get('status') || '';
  const region = searchParams.get('region') || '';
  const author = searchParams.get('author') || '';
  const updated = searchParams.get('updated') || '';
  const sort = searchParams.get('sort') || 'updated';
  const page = Number(searchParams.get('page')) || 1;

  const authors = useMemo(() => Array.from(new Set(comics.map((item) => item.author))), []);

  const setParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page');
    setSearchParams(next);
  };

  const resetFilters = () => {
    const next = new URLSearchParams();
    if (sort !== 'updated') next.set('sort', sort);
    setSearchParams(next);
  };

  const filtered = useMemo(() => {
    const list = comics.filter((comic) => {
      if (genre && comic.genre !== genre) return false;
      if (status && comic.status !== status) return false;
      if (region && comic.region !== region) return false;
      if (author && comic.author !== author) return false;
      if (updated) {
        const limit = Number(updated);
        if (daysBetween(comic.updatedAt) > limit) return false;
      }
      return true;
    });
    list.sort((a, b) => {
      if (sort === 'popularity') return b.popularity - a.popularity;
      if (sort === 'favorites') return b.favorites - a.favorites;
      return a.updatedAt < b.updatedAt ? 1 : -1;
    });
    return list;
  }, [genre, status, region, author, updated, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage = Math.min(page, totalPages);
  const pageItems = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  const hasFilter = Boolean(genre || status || region || author || updated);
  const activeChips = [
    genre && { key: 'genre', label: `类型：${genre}` },
    status && { key: 'status', label: `状态：${status}` },
    region && { key: 'region', label: `地区：${region}` },
    author && { key: 'author', label: `作者：${author}` },
    updated && { key: 'updated', label: `更新：${updated === '7' ? '近一周' : '近一月'}` },
  ].filter(Boolean) as { key: string; label: string }[];

  const filterContent = (
    <div className="flex flex-col gap-5">
      <FilterGroup title="漫画类型" options={GENRES} value={genre} onSelect={(v) => setParam('genre', v === genre ? '' : v)} />
      <FilterGroup title="连载状态" options={STATUS} value={status} onSelect={(v) => setParam('status', v === status ? '' : v)} />
      <FilterGroup title="地区" options={REGIONS} value={region} onSelect={(v) => setParam('region', v === region ? '' : v)} />
      <FilterGroup
        title="更新日期"
        options={UPDATED.map((u) => u.label)}
        value={UPDATED.find((u) => String(u.days) === updated)?.label || ''}
        onSelect={(label) => {
          const target = UPDATED.find((u) => u.label === label);
          if (!target) return;
          setParam('updated', updated === String(target.days) ? '' : String(target.days));
        }}
      />
      <div>
        <h3 className="mb-2.5 text-sm font-700 text-foreground-800">作者</h3>
        <div className="flex max-h-40 flex-col gap-1 overflow-y-auto pr-1">
          <button
            type="button"
            onClick={() => setParam('author', '')}
            className={`cursor-pointer rounded-md px-2.5 py-1.5 text-left text-sm font-label transition-colors ${
              !author ? 'bg-primary-100 text-primary-800' : 'text-foreground-600 hover:bg-background-100'
            }`}
          >
            全部作者
          </button>
          {authors.map((name) => (
            <button
              key={name}
              type="button"
              onClick={() => setParam('author', name === author ? '' : name)}
              className={`cursor-pointer rounded-md px-2.5 py-1.5 text-left text-sm font-label transition-colors ${
                name === author ? 'bg-primary-100 text-primary-800' : 'text-foreground-600 hover:bg-background-100'
              }`}
            >
              {name}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <title>漫画分类 - 漫蛙动漫频道</title>
      <meta
        name="description"
        content="漫蛙漫画分类页，按类型、连载状态、地区、作者与更新时间自由筛选漫画作品，涵盖热血、科幻、恋爱、日常、奇幻等多种题材。"
      />
      <meta name="keywords" content="漫蛙,漫画分类,漫画筛选,漫画推荐,连载漫画,国漫,日漫" />

      <SiteHeader />

      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
            <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '漫画分类' }]} />
            <h1 className="mt-3 font-heading text-2xl font-800 text-foreground-950 md:text-3xl">漫画分类</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">
              这里汇集了漫蛙收录的全部漫画作品。按类型、连载状态、地区、作者与更新时间筛选，快速找到你想看的漫画。
            </p>
          </div>
        </div>

        <div className="mx-auto w-full max-w-[1440px] px-4 py-8 md:px-8 md:py-10">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[240px_1fr]">
            <aside className="hidden lg:block">
              <div className="sticky top-24 rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="mb-4 flex items-center gap-2 font-heading text-base font-800 text-foreground-950">
                  <i className="ri-filter-3-line text-primary-600" />
                  筛选条件
                </h2>
                {filterContent}
                {hasFilter && (
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-5 w-full cursor-pointer whitespace-nowrap rounded-md border border-background-300 py-2 text-sm font-label font-500 text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-700"
                  >
                    重置筛选
                  </button>
                )}
              </div>
            </aside>

            <div>
              <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={() => setMobileFilterOpen((v) => !v)}
                    className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3.5 py-2 text-sm font-label text-foreground-700 lg:hidden"
                  >
                    <i className="ri-filter-3-line" />
                    筛选
                  </button>
                  <span className="text-sm font-label text-foreground-500">
                    共 <strong className="font-700 text-primary-700">{filtered.length}</strong> 部作品
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {SORTS.map((item) => (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => setParam('sort', item.key)}
                      className={`cursor-pointer whitespace-nowrap rounded-md px-3.5 py-2 text-sm font-label transition-colors ${
                        sort === item.key
                          ? 'bg-primary-500 text-background-50'
                          : 'bg-background-100 text-foreground-600 hover:text-foreground-950'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              {hasFilter && (
                <div className="mb-5 flex flex-wrap items-center gap-2">
                  {activeChips.map((chip) => (
                    <button
                      key={chip.key}
                      type="button"
                      onClick={() => setParam(chip.key, '')}
                      className="flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-3 py-1 text-xs font-label text-accent-900 transition-colors hover:bg-accent-200"
                    >
                      {chip.label}
                      <i className="ri-close-line" />
                    </button>
                  ))}
                </div>
              )}

              {mobileFilterOpen && (
                <div className="mb-5 rounded-lg border border-background-200 bg-background-50 p-5 lg:hidden">
                  <div className="mb-4 flex items-center justify-between">
                    <span className="font-heading text-base font-700 text-foreground-950">筛选条件</span>
                    <button
                      type="button"
                      onClick={() => setMobileFilterOpen(false)}
                      aria-label="关闭筛选"
                      className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                  {filterContent}
                  <button
                    type="button"
                    onClick={resetFilters}
                    className="mt-5 w-full cursor-pointer whitespace-nowrap rounded-md border border-background-300 py-2 text-sm font-label font-500 text-foreground-700"
                  >
                    重置筛选
                  </button>
                </div>
              )}

              {pageItems.length === 0 ? (
                <EmptyState onReset={resetFilters} />
              ) : (
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                  {pageItems.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} layout="row" />
                  ))}
                </div>
              )}

              <Pagination page={safePage} totalPages={totalPages} onChange={(p) => setParam('page', String(p))} />
            </div>
          </div>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}

function FilterGroup({
  title,
  options,
  value,
  onSelect,
}: {
  title: string;
  options: string[];
  value: string;
  onSelect: (value: string) => void;
}) {
  return (
    <div>
      <h3 className="mb-2.5 text-sm font-700 text-foreground-800">{title}</h3>
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => onSelect(option)}
            className={`cursor-pointer whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs font-label transition-colors ${
              value === option
                ? 'bg-primary-500 text-background-50'
                : 'bg-background-100 text-foreground-600 hover:bg-background-200'
            }`}
          >
            {option}
          </button>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ onReset }: { onReset: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 py-20 text-center">
      <span className="mw-float flex h-16 w-16 items-center justify-center rounded-full bg-primary-100 text-3xl text-primary-600">
        <i className="ri-search-eye-line" />
      </span>
      <h3 className="mt-4 font-heading text-lg font-700 text-foreground-950">暂无符合筛选条件的漫画作品</h3>
      <p className="mt-2 text-sm font-label text-foreground-500">试试调整筛选条件，或直接重置看看全部作品</p>
      <button
        type="button"
        onClick={onReset}
        className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-500 text-background-50 transition-colors hover:bg-primary-600"
      >
        重置筛选
      </button>
      <Link
        to="/category/news"
        className="mt-3 cursor-pointer text-sm font-label text-accent-700 underline-offset-4 hover:underline"
      >
        或者去看看动漫资讯
      </Link>
    </div>
  );
}